import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, CardMedia, Button, Grid, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel, Select, MenuItem, Snackbar, Tooltip, useTheme, useMediaQuery
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import AddIcon from '@mui/icons-material/AddOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CloseIcon from '@mui/icons-material/Close';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined';
import api from '../api';
import { PHOTO_UPLOAD_TIMEOUT_MS, photoFormData } from '../utils/photoUpload';
import useAuth from '../hooks/useAuth';
import ContractorAssignment from './ContractorAssignment';
import PhotoUpload from './PhotoUpload';
import CostTracking from './CostTracking';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const formatScheduleDay = (dateStr) => {
  const [y, m, d] = String(dateStr).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const MaintenanceDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [addRequestDialog, setAddRequestDialog] = useState(false);
  const isContractor = user?.role === 'Contractor';
  const [editRequestDialog, setEditRequestDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [editingRequest, setEditingRequest] = useState({
    title: '',
    description: '',
    priority: 'medium',
    propertyId: '',
    unitId: '',
    category: 'general'
  });
  const [editingPhotos, setEditingPhotos] = useState([]);
  const [contractorAssignmentDialog, setContractorAssignmentDialog] = useState(false);
  const [selectedRequestForAssignment, setSelectedRequestForAssignment] = useState(null);
  const [costTrackingDialog, setCostTrackingDialog] = useState(false);
  const [selectedRequestForCosts, setSelectedRequestForCosts] = useState(null);
  const [newRequest, setNewRequest] = useState({
    title: '',
    description: '',
    priority: '', // Empty = auto-detect on backend
    propertyId: '',
    unitId: '',
    category: 'general'
  });
  const [requestPhotos, setRequestPhotos] = useState([]);

  useEffect(() => {
    fetchRequests();
    fetchProperties();
  }, []);

  const fetchRequests = async () => {
    try {
      const res = await api.get('/api/maintenance-requests');
      setRequests(res.data);
      
      // Debug: Log the structure of the first request with photos
      const requestWithPhotos = res.data.find(req => req.photos && req.photos.length > 0);
      if (requestWithPhotos) {
        console.log('🔍 Sample request with photos:', {
          id: requestWithPhotos.id,
          title: requestWithPhotos.title,
          photosCount: requestWithPhotos.photos.length,
          firstPhoto: requestWithPhotos.photos[0],
          createdAt: requestWithPhotos.createdAt,
          createdAtType: typeof requestWithPhotos.createdAt
        });
        console.log('🔍 Full first photo object:', JSON.stringify(requestWithPhotos.photos[0], null, 2));
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to fetch maintenance requests', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchProperties = async () => {
    try {
      const res = await api.get('/api/properties');
      setProperties(res.data);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to fetch properties', severity: 'error' });
    }
  };

  const fetchUnits = (propertyId) => {
    try {
      // Find the selected property and get its units
      const property = properties.find(p => p.id === propertyId);
      if (property && property.units) {
        setUnits(property.units);
      } else {
        setUnits([]);
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to fetch units', severity: 'error' });
    }
  };

  const handleAddRequest = async () => {
    try {
      const { priority, ...rest } = newRequest;
      let uploadedPhotos = [];
      for (const photo of requestPhotos) {
        if (photo.file) {
          const formData = await photoFormData(photo.file);
          const uploadResponse = await api.post('/api/photos/upload-photo', formData, {
            timeout: PHOTO_UPLOAD_TIMEOUT_MS
          });
          uploadedPhotos.push({
            id: uploadResponse.data.id,
            url: uploadResponse.data.url,
            filename: uploadResponse.data.filename
          });
        } else if (photo.url && (photo.url.startsWith('http://') || photo.url.startsWith('https://'))) {
          uploadedPhotos.push({
            id: photo.id != null ? String(photo.id) : undefined,
            url: photo.url,
            filename: photo.filename || photo.name || ''
          });
        }
      }

      const requestData = {
        ...rest,
        photos: uploadedPhotos,
        tenantId: 'admin-created' // Mark as admin-created for tracking
      };
      // Only send priority if user explicitly selected one (enables auto-detect when empty)
      if (priority) requestData.priority = priority;

      await api.post('/api/maintenance-requests', requestData);
      setSnackbar({ open: true, message: 'Maintenance request submitted successfully!', severity: 'success' });
      setAddRequestDialog(false);
      setNewRequest({
        title: '',
        description: '',
        priority: '',
        propertyId: '',
        unitId: '',
        category: 'general'
      });
      setRequestPhotos([]);
      fetchRequests();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to submit maintenance request', severity: 'error' });
    }
  };

  const handleUpdateStatus = async (requestId, newStatus) => {
    try {
      const res = await api.put(`/api/maintenance-requests/${requestId}`, { status: newStatus });
      const nextScheduled = res?.data?.scheduledTask?.nextDueDate;
      
      // Send notification for status change
      try {
        await api.post('/api/notifications/maintenance', {
          requestId,
          notificationType: 'Status Update',
          message: `Maintenance request status changed to: ${newStatus.replace('_', ' ')}`
        });
      } catch (notificationError) {
        console.error('Failed to send notification:', notificationError);
      }
      
      setSnackbar({
        open: true,
        message: nextScheduled
          ? `Marked completed. The next one is scheduled for ${formatScheduleDay(nextScheduled)}.`
          : 'Status updated successfully!',
        severity: 'success'
      });
      fetchRequests();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update status', severity: 'error' });
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this maintenance request?')) return;
    try {
      await api.delete(`/api/maintenance-requests/${requestId}`);
      setSnackbar({ open: true, message: 'Maintenance request deleted successfully!', severity: 'success' });
      fetchRequests();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete maintenance request', severity: 'error' });
    }
  };

  const handleAssignContractor = (request) => {
    setSelectedRequestForAssignment(request);
    setContractorAssignmentDialog(true);
  };

  const handleAssignmentUpdate = async (assignmentData) => {
    try {
      // Send notification for contractor assignment
      if (assignmentData) {
        await api.post('/api/notifications/maintenance', {
          requestId: selectedRequestForAssignment?.id,
          notificationType: 'Contractor Assignment',
          message: `Contractor assigned to maintenance request`
        });
      }
    } catch (notificationError) {
      console.error('Failed to send notification:', notificationError);
    }
    
    setSnackbar({ open: true, message: 'Contractor assignment updated successfully!', severity: 'success' });
    fetchRequests();
  };

  const handleTrackCosts = (request) => {
    setSelectedRequestForCosts(request);
    setCostTrackingDialog(true);
  };

  const handleCostUpdate = () => {
    setSnackbar({ open: true, message: 'Costs updated successfully!', severity: 'success' });
    fetchRequests();
  };

  const handleEditRequest = (request) => {
    setSelectedRequest(request);
    setEditingRequest({
      title: request.title,
      description: request.description,
      priority: request.priority,
      propertyId: request.propertyId,
      unitId: request.unitId,
      category: request.category
    });
    setEditingPhotos(request.photos || []);
    fetchUnits(request.propertyId);
    setEditRequestDialog(true);
  };

  const handleUpdateRequest = async () => {
    try {
      // First upload any new photos that have files
      let uploadedPhotos = [];
      for (const photo of editingPhotos) {
        if (photo.file) {
          // This is a new photo that needs to be uploaded
          const formData = await photoFormData(photo.file, { uploadedBy: 'admin' });
          const uploadResponse = await api.post('/api/photos/upload-photo', formData, {
            timeout: PHOTO_UPLOAD_TIMEOUT_MS
          });
          uploadedPhotos.push({
            id: uploadResponse.data.id,
            url: uploadResponse.data.url,
            filename: uploadResponse.data.filename
          });
        } else {
          // This is an existing photo, keep it as is
          uploadedPhotos.push(photo);
        }
      }

      const updateData = {
        ...editingRequest,
        photos: uploadedPhotos,
        updatedAt: new Date()
      };
      
      console.log('Updating maintenance request:', selectedRequest.id, updateData);
      await api.put(`/api/maintenance-requests/${selectedRequest.id}`, updateData);
      setSnackbar({ open: true, message: 'Maintenance request updated successfully!', severity: 'success' });
      setEditRequestDialog(false);
      setSelectedRequest(null);
      setEditingRequest({
        title: '',
        description: '',
        priority: 'medium',
        propertyId: '',
        unitId: '',
        category: 'general'
      });
      setEditingPhotos([]);
      fetchRequests();
    } catch (err) {
      console.error('Error updating maintenance request:', err);
      setSnackbar({ open: true, message: `Failed to update maintenance request: ${err.response?.data?.message || err.message}`, severity: 'error' });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'submitted': return 'default';
      case 'assigned': return 'primary';
      case 'in_progress': return 'warning';
      case 'completed': return 'success';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'submitted': return <AssignmentOutlinedIcon />;
      case 'assigned': return <BuildOutlinedIcon />;
      case 'in_progress': return <BuildOutlinedIcon />;
      case 'completed': return <CheckCircleOutlineIcon />;
      default: return <AssignmentOutlinedIcon />;
    }
  };

  const getPropertyName = (propertyId) => {
    const property = properties.find(p => p.id === propertyId);
    return property ? property.name : 'Unknown Property';
  };

  const getUnitName = (propertyId, unitId, unitNameFromRequest) => {
    console.log('🔍 getUnitName called with:', { propertyId, unitId, unitNameFromRequest });
    // First try to use unitName if it was sent from the request (Tenant Portal)
    if (unitNameFromRequest) {
      console.log('✅ Using unitNameFromRequest:', unitNameFromRequest);
      return unitNameFromRequest;
    }
    // Otherwise, try to find the unit in the units array
    const unit = units.find(u => u.id === unitId);
    console.log('🔍 Found unit in array:', unit);
    return unit ? unit.name : 'Unknown Unit';
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 2, sm: 3, md: 4 } }}><Typography>Loading maintenance requests...</Typography></Box>;
  }

  return (
    <Box sx={{ mt: { xs: 2, sm: 3, md: 4 }, p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: { xs: 2, sm: 3 },
        gap: { xs: 2, sm: 0 }
      }}>
        <Typography 
          variant="h4" 
          gutterBottom
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, mb: { xs: 1, sm: 0 } }}
        >
          <BuildOutlinedIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Maintenance Requests
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setRequestPhotos([]);
            setAddRequestDialog(true);
          }}
          style={{ display: isContractor ? 'none' : 'flex' }}
          fullWidth={isMobile}
          size={isMobile ? 'small' : 'medium'}
        >
          {isMobile ? 'Submit' : 'Submit Request'}
        </Button>
      </Box>

      <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
        {requests.map((request) => (
          <Grid item xs={12} md={6} lg={4} key={request.id}>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start', 
                  mb: { xs: 1.5, sm: 2 },
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 1, sm: 0 }
                }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                  >
                    {request.title}
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'row', sm: 'row' },
                    gap: { xs: 0.5, sm: 0 }
                  }}>
                    <Tooltip title="Edit Request">
                      <IconButton 
                        size={isMobile ? 'small' : 'medium'} 
                        sx={{ mr: { xs: 0.5, sm: 1 } }} 
                        onClick={() => handleEditRequest(request)}
                        aria-label="Edit request"
                      >
                        <EditOutlinedIcon fontSize={isMobile ? 'small' : 'medium'} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Request">
                      <IconButton 
                        size={isMobile ? 'small' : 'medium'} 
                        color="error" 
                        onClick={() => handleDeleteRequest(request.id)}
                        aria-label="Delete request"
                      >
                        <DeleteOutlinedIcon fontSize={isMobile ? 'small' : 'medium'} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  gutterBottom
                  sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                >
                  {request.description}
                </Typography>

                {request.photos && request.photos.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Photos ({request.photos.length})
                    </Typography>
                    <Grid container spacing={1}>
                      {request.photos.slice(0, 3).map((photo, index) => {
                        console.log('🔍 Photo object structure:', { 
                          index, 
                          photo: JSON.stringify(photo, null, 2),
                          hasUrl: !!photo.url, 
                          urlType: typeof photo.url,
                          hasPreview: !!photo.preview,
                          previewType: typeof photo.preview,
                          hasId: !!photo.id,
                          idType: typeof photo.id
                        });
                        // Handle different photo URL formats
                        let imageUrl = null;
                        if (photo.url && photo.url.startsWith('http')) {
                          // Full URL (from Firebase Storage)
                          imageUrl = photo.url;
                        } else if (photo.url) {
                          // Relative URL (append to API base URL)
                          imageUrl = `${api.defaults.baseURL}${photo.url}`;
                        } else if (photo.preview && photo.preview.startsWith('data:')) {
                          // Base64 preview URL (local preview)
                          imageUrl = photo.preview;
                        }
                        console.log('🖼️ Rendering photo:', { id: photo.id, url: photo.url, preview: photo.preview, imageUrl, filename: photo.filename });
                        
                        // Skip photos without valid URLs
                        if (!imageUrl) {
                          console.log('⚠️ Skipping photo without valid URL:', photo);
                          return null;
                        }
                        
                        // Use a simple img tag instead of CardMedia to avoid MUI issues
                        return (
                          <Grid item xs={4} key={photo.id || index}>
                            <Card sx={{ height: 80, overflow: 'hidden' }}>
                              <Box
                                component="img"
                                src={imageUrl}
                                alt={photo.filename || photo.name || `Photo ${index + 1}`}
                                sx={{
                                  width: '100%',
                                  height: 80,
                                  objectFit: 'cover',
                                  display: 'block'
                                }}
                                onError={(e) => {
                                  console.error('❌ Image load error:', e.target.src);
                                  // Replace with placeholder image
                                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCA4MCA4MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjgwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0yNSAzNUg1NVY0NUgyNVYzNVoiIGZpbGw9IiNEOUQ5RDkiLz4KPHBhdGggZD0iTTMwIDQwSDQ1VjQ1SDMwVjQwWiIgZmlsbD0iI0Q5RDlEOSIvPgo8dGV4dCB4PSI0MCIgeT0iNTUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMCIgZmlsbD0iIzk5OTk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSI+UGhvdG8gTm90IEZvdW5kPC90ZXh0Pgo8L3N2Zz4K';
                                }}
                                onLoad={() => console.log('✅ Image loaded successfully:', imageUrl)}
                              />
                            </Card>
                          </Grid>
                        );
                      })}
                      {request.photos.length > 3 && (
                        <Grid item xs={4}>
                          <Card sx={{ height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography variant="caption">
                              +{request.photos.length - 3} more
                            </Typography>
                          </Card>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                )}

                <Box sx={{ 
                  display: 'flex', 
                  gap: { xs: 0.5, sm: 1 }, 
                  mb: { xs: 1.5, sm: 2 }, 
                  flexWrap: 'wrap' 
                }}>
                  <Chip
                    icon={getStatusIcon(request.status)}
                    label={request.status.replace('_', ' ')}
                    color={getStatusColor(request.status)}
                    size="small"
                  />
                  <Chip
                    label={request.priority}
                    color={getPriorityColor(request.priority)}
                    size="small"
                  />
                  {request.priorityAutoDetected && (
                    <Chip
                      icon={<AutoAwesomeOutlinedIcon sx={{ fontSize: 14 }} />}
                      label="Auto-detected"
                      color={getPriorityColor(request.priority)}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  <Chip
                    label={request.category}
                    variant="outlined"
                    size="small"
                  />
                  {request.source === 'scheduledMaintenance' && (
                    <Tooltip title="Created automatically from Scheduled Maintenance. Completing it moves the schedule to the next date.">
                      <Chip
                        icon={<EventRepeatOutlinedIcon sx={{ fontSize: 14 }} />}
                        label="From schedule"
                        size="small"
                        variant="outlined"
                        color="info"
                      />
                    </Tooltip>
                  )}
                </Box>

                <Typography variant="caption" display="block" gutterBottom>
                  <strong>Property:</strong> {getPropertyName(request.propertyId)}
                </Typography>
                <Typography variant="caption" display="block" gutterBottom>
                  <strong>Unit:</strong> {getUnitName(request.propertyId, request.unitId, request.unitName)}
                </Typography>
                <Typography variant="caption" display="block" gutterBottom>
                  <strong>Submitted:</strong> {request.createdAt ? (
                    // Handle both ISO string and Firestore timestamp format
                    typeof request.createdAt === 'string' 
                      ? new Date(request.createdAt).toLocaleDateString()
                      : request.createdAt._seconds 
                        ? new Date(request.createdAt._seconds * 1000).toLocaleDateString()
                        : 'Unknown'
                  ) : 'Unknown'}
                </Typography>

                {request.contractorId && (
                  <Box sx={{ mt: 1, p: 1, bgcolor: 'primary.light', borderRadius: 1 }}>
                    <Typography variant="caption" display="flex" alignItems="center">
                      <PersonOutlineIcon sx={{ mr: 0.5, fontSize: 16 }} />
                      <strong>Assigned to:</strong> {request.contractorName || 'Contractor'}
                    </Typography>
                    {request.assignmentNotes && (
                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                        <strong>Notes:</strong> {request.assignmentNotes}
                      </Typography>
                    )}
                  </Box>
                )}

                <Box sx={{ mt: { xs: 1.5, sm: 2 } }}>
                  <Typography 
                    variant="subtitle2" 
                    gutterBottom
                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                  >
                    Actions:
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: { xs: 0.5, sm: 1 }, 
                    flexWrap: 'wrap', 
                    mb: { xs: 0.75, sm: 1 },
                    flexDirection: { xs: 'column', sm: 'row' }
                  }}>
                    <Tooltip title="Assign Contractor">
                      <Button
                        size={isMobile ? 'small' : 'medium'}
                        variant="outlined"
                        startIcon={<AssignmentOutlinedIcon />}
                        onClick={() => handleAssignContractor(request)}
                        fullWidth={isMobile}
                      >
                        {request.contractorId ? 'Reassign' : 'Assign'}
                      </Button>
                    </Tooltip>
                    <Tooltip title="Track Costs">
                      <Button
                        size={isMobile ? 'small' : 'medium'}
                        variant="outlined"
                        startIcon={<AttachMoneyOutlinedIcon />}
                        onClick={() => handleTrackCosts(request)}
                        fullWidth={isMobile}
                      >
                        {isMobile ? 'Costs' : 'Track Costs'}
                      </Button>
                    </Tooltip>
                  </Box>
                  <Typography 
                    variant="subtitle2" 
                    gutterBottom
                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                  >
                    Update Status:
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: { xs: 0.5, sm: 1 }, 
                    flexWrap: 'wrap',
                    flexDirection: { xs: 'column', sm: 'row' }
                  }}>
                    {['submitted', 'assigned', 'in_progress', 'completed'].map((status) => (
                      <Button
                        key={status}
                        size={isMobile ? 'small' : 'medium'}
                        variant={request.status === status ? 'contained' : 'outlined'}
                        onClick={() => handleUpdateStatus(request.id, status)}
                        disabled={request.status === status}
                        fullWidth={isMobile}
                      >
                        {status.replace('_', ' ')}
                      </Button>
                    ))}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Add Request Dialog */}
      <Dialog 
        open={addRequestDialog} 
        onClose={() => setAddRequestDialog(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Submit Maintenance Request
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <TextField
            label="Title"
            value={newRequest.title}
            onChange={(e) => setNewRequest({ ...newRequest, title: e.target.value })}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Description"
            value={newRequest.description}
            onChange={(e) => setNewRequest({ ...newRequest, description: e.target.value })}
            fullWidth
            margin="dense"
            multiline
            rows={3}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Priority</InputLabel>
            <Select
              value={newRequest.priority || 'auto'}
              label="Priority"
              onChange={(e) => setNewRequest({ ...newRequest, priority: e.target.value === 'auto' ? '' : e.target.value })}
            >
              <MenuItem value="auto">Auto-detect (recommended)</MenuItem>
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Category</InputLabel>
            <Select
              value={newRequest.category}
              label="Category"
              onChange={(e) => setNewRequest({ ...newRequest, category: e.target.value })}
            >
              <MenuItem value="general">General</MenuItem>
              <MenuItem value="plumbing">Plumbing</MenuItem>
              <MenuItem value="electrical">Electrical</MenuItem>
              <MenuItem value="hvac">HVAC</MenuItem>
              <MenuItem value="appliance">Appliance</MenuItem>
              <MenuItem value="structural">Structural</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Property</InputLabel>
            <Select
              value={newRequest.propertyId}
              label="Property"
              onChange={(e) => {
                setNewRequest({ ...newRequest, propertyId: e.target.value, unitId: '' });
                fetchUnits(e.target.value);
              }}
            >
              {properties.map((property) => (
                <MenuItem key={property.id} value={property.id}>{property.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Unit</InputLabel>
            <Select
              value={newRequest.unitId}
              label="Unit"
              onChange={(e) => setNewRequest({ ...newRequest, unitId: e.target.value })}
              disabled={!newRequest.propertyId}
            >
              {units.map((unit) => (
                <MenuItem key={unit.id} value={unit.id}>{unit.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ mt: 2 }}>
            <PhotoUpload
              onPhotosChange={setRequestPhotos}
              maxPhotos={5}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => setAddRequestDialog(false)}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddRequest} 
            variant="contained"
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            {isMobile ? 'Submit' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Request Dialog */}
      <Dialog 
        open={editRequestDialog} 
        onClose={() => setEditRequestDialog(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Edit Maintenance Request
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <TextField
            label="Title"
            value={editingRequest.title}
            onChange={(e) => setEditingRequest({ ...editingRequest, title: e.target.value })}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Description"
            value={editingRequest.description}
            onChange={(e) => setEditingRequest({ ...editingRequest, description: e.target.value })}
            fullWidth
            margin="dense"
            multiline
            rows={3}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Priority</InputLabel>
            <Select
              value={editingRequest.priority}
              label="Priority"
              onChange={(e) => setEditingRequest({ ...editingRequest, priority: e.target.value })}
            >
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Category</InputLabel>
            <Select
              value={editingRequest.category}
              label="Category"
              onChange={(e) => setEditingRequest({ ...editingRequest, category: e.target.value })}
            >
              <MenuItem value="general">General</MenuItem>
              <MenuItem value="plumbing">Plumbing</MenuItem>
              <MenuItem value="electrical">Electrical</MenuItem>
              <MenuItem value="hvac">HVAC</MenuItem>
              <MenuItem value="appliance">Appliance</MenuItem>
              <MenuItem value="structural">Structural</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Property</InputLabel>
            <Select
              value={editingRequest.propertyId}
              label="Property"
              onChange={(e) => {
                setEditingRequest({ ...editingRequest, propertyId: e.target.value, unitId: '' });
                fetchUnits(e.target.value);
              }}
            >
              {properties.map((property) => (
                <MenuItem key={property.id} value={property.id}>{property.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Unit</InputLabel>
            <Select
              value={editingRequest.unitId}
              label="Unit"
              onChange={(e) => setEditingRequest({ ...editingRequest, unitId: e.target.value })}
              disabled={!editingRequest.propertyId}
            >
              {units.map((unit) => (
                <MenuItem key={unit.id} value={unit.id}>{unit.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ mt: 2 }}>
            <PhotoUpload
              onPhotosChange={setEditingPhotos}
              maxPhotos={5}
              existingPhotos={editingPhotos}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => setEditRequestDialog(false)}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateRequest} 
            variant="contained"
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            {isMobile ? 'Update' : 'Update Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Contractor Assignment Dialog */}
      <ContractorAssignment
        open={contractorAssignmentDialog}
        onClose={() => setContractorAssignmentDialog(false)}
        requestId={selectedRequestForAssignment?.id}
        currentAssignment={selectedRequestForAssignment ? {
          contractorId: selectedRequestForAssignment.contractorId,
          notes: selectedRequestForAssignment.assignmentNotes,
          assignedAt: selectedRequestForAssignment.assignedAt
        } : null}
        onAssignmentUpdate={handleAssignmentUpdate}
      />

      {/* Cost Tracking Dialog */}
      <Dialog 
        open={costTrackingDialog} 
        onClose={() => setCostTrackingDialog(false)} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              <AttachMoneyOutlinedIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Cost Tracking - {selectedRequestForCosts?.title}
            </Typography>
            <IconButton onClick={() => setCostTrackingDialog(false)} size="small" aria-label="Close cost tracking">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRequestForCosts && (
            <CostTracking
              requestId={selectedRequestForCosts.id}
              onCostUpdate={handleCostUpdate}
            />
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MaintenanceDashboard; 